import { useCallback, useRef, useState } from 'preact/hooks';
import { fetchOnce, prepareBody } from '@shared/hooks/http';
import { apiBase } from '../api/api-base';
import { toast } from 'react-toastify';
import { objectToFormData } from '../helpers/object-to-form-data';

export function usePut(
    endpoint,
    {
        asFormData = false,
        autoCancelPrevious = true,
        successMessage = 'Successfully captured data!',
    } = {}
) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const controllerRef = useRef(null);

    const cancel = useCallback(() => {
        try {
            controllerRef.current?.abort();
        } finally {
            controllerRef.current = null;
        }
    }, []);

    const put = useCallback(
        async (body, config = {}) => {
            if (autoCancelPrevious && controllerRef.current) {
                controllerRef.current.abort();
                controllerRef.current = null;
            }

            const controller = new AbortController();
            controllerRef.current = controller;

            setIsSubmitting(true);
            setError(null);
            setData(null);
            try {
                // objectToFormData recursively flattens arrays/objects into bracket-notation keys
                // (e.g. rating[0][value]) that ASP.NET Core's form binder understands - a plain
                // fd.append(key, value) stringifies non-primitive values like the rating array to
                // "[object Object]", which the server can't bind and treats as no rating submitted.
                const rawBody = asFormData ? objectToFormData(body ?? {}) : body;

                const { body: prepared, headers } = prepareBody(rawBody, config.headers ?? {});

                const result = await fetchOnce(`${apiBase}${endpoint}`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers,
                    body: prepared,
                    signal: config.signal ?? controller.signal,
                });

                const resolved = result ?? { success: true };
                setData(resolved);
                toast.success(successMessage);
                return resolved;
            } catch (err) {
                if (err?.name === 'AbortError') return Promise.reject(err);
                toast.error(err?.body?.detail ?? err?.message ?? 'Something went wrong!');
                setError(err);
                throw err;
            } finally {
                setIsSubmitting(false);
            }
        },
        [endpoint, asFormData, autoCancelPrevious]
    );

    return { put, cancel, isSubmitting, error, data };
}
